/** @jsx jsx */
import React, { useEffect, useRef } from 'react';
import { jsx } from '@emotion/core';
import {
  Box,
  debounce,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ReplayIcon from '@material-ui/icons/Replay';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { brandsSelector, rangesSelector } from 'reducers/product.reducer';
import { productBrands, productRanges } from 'actions/product.actions';
import { getSelectedItems, withNotIndicatedFilter } from '../utils';
import { ResetButton } from '../styles';
import MultiSelect, { formattedSelect } from '../MultiSelect';
import {
  ADD,
  CHANGE_SEARCH,
  StateType,
  FilterKeyType,
  REMOVE,
  RESET,
  ActionType,
} from '../filter.reducer';
import { ICustomTheme } from 'interfaces';
import styled from '@emotion/styled';
import { Chips } from '../Chips';
import { PrimaryButton } from 'components/Button';

const debounceSearch = debounce(
  (getProducts: () => void) => getProducts(),
  500,
);

export const StyledTextField = styled(TextField)<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  // input label when focused
  '& label.Mui-focused': {
    color: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='standard'
  '& .MuiInput-underline:after': {
    borderBottomColor: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='filled'
  '& .MuiFilledInput-underline:after': {
    borderBottomColor: theme.colors.paletteFromBO.main,
  },
  // focused color for input with variant='outlined'
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.paletteFromBO.main,
    },
  },
}));

const Filter = ({
  challengeId,
  onConfirm,
  isMobile,
  getProducts,
  numberOfProducts,
  filters,
  filtersDispatch,
}: {
  challengeId: string;
  onConfirm?: () => void;
  isMobile: boolean;
  getProducts: () => void;
  numberOfProducts?: number;
  filters: StateType;
  filtersDispatch: (action: ActionType) => void;
}): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mountRef = useRef(false);

  const { data: brandList } = useSelector(brandsSelector);
  const { data: rangeList } = useSelector(rangesSelector);

  const brandItems = withNotIndicatedFilter(
    formattedSelect(brandList, 'brand'),
    t('challenges.declaration.brandNotIndicatedFilter'),
  );
  const rangeItems = withNotIndicatedFilter(
    formattedSelect(rangeList, 'range'),
    t('challenges.declaration.rangeNotIndicatedFilter'),
  );

  const selectedBrandItems = getSelectedItems(filters.brands, brandItems);
  const selectedRangeItems = getSelectedItems(filters.ranges, rangeItems);

  const areProductsFiltered = Object.values(filters).some(
    (item) => item.length,
  );

  useEffect(() => {
    dispatch(productBrands.request({ challengeId }));
    dispatch(productRanges.request({ challengeId }));
  }, [challengeId, dispatch]);

  // For these 2 useEffect:
  // we already have products called on previous page
  // we use mountRef since we dont't want to call products as
  // long as filters isn't triggered
  useEffect(() => {
    if (mountRef.current) {
      getProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.brands, filters.ranges]);

  useEffect(() => {
    if (mountRef.current) {
      debounceSearch(getProducts);
    } else {
      mountRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const handleDelete =
    (filterKey: FilterKeyType) => (keyWordToDelete: string) => {
      filtersDispatch({
        type: REMOVE,
        value: keyWordToDelete,
        filterKey,
      });
    };
  const handleMultiSelectChange =
    (filterKey: FilterKeyType) => (selected: any) => {
      filtersDispatch({
        type: ADD,
        value: selected,
        filterKey,
      });
    };
  const handleChangeSearchKey = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    filtersDispatch({
      type: CHANGE_SEARCH,
      value: event?.target?.value,
    });
  };
  const handleReset = () => {
    filtersDispatch({ type: RESET });
  };
  const handleResetSearch = () => {
    filtersDispatch({
      type: CHANGE_SEARCH,
      value: '',
    });
  };

  return (
    <React.Fragment>
      <Grid
        container
        css={{
          margin: !isMobile ? theme.spacing(4, 0, 0) : theme.spacing(2, 0),
        }}
      >
        <Grid item xs>
          <Typography variant="body2" align={!isMobile ? 'left' : 'center'}>
            <Box fontWeight="fontWeightBold">
              {t('challenges.declaration.searchProduct')}
            </Box>
          </Typography>
        </Grid>
      </Grid>
      <Grid container alignContent="center" alignItems="flex-end" spacing={2}>
        <Grid item xs={!isMobile ? 4 : 12}>
          <StyledTextField
            variant="outlined"
            autoComplete="outlined"
            name="search"
            required
            value={filters.search}
            fullWidth
            placeholder={t('challenges.declaration.searchByCodeLabel')}
            onChange={handleChangeSearchKey}
            css={{ marginBottom: theme.spacing(2) }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Divider css={{ width: '100%' }} />
        </Grid>
        <Grid item xs={!isMobile ? true : 12}>
          <MultiSelect
            label={t('challenges.declaration.brands')}
            items={brandItems}
            onChange={handleMultiSelectChange('brands')}
            selectedItems={filters.brands}
          />
          {isMobile && (
            <Chips
              list={selectedBrandItems}
              handleDelete={handleDelete('brands')}
            />
          )}
          <Divider css={{ width: '100%' }} />
        </Grid>
        <Grid item xs={!isMobile ? true : 12}>
          <MultiSelect
            label={t('challenges.declaration.ranges')}
            items={rangeItems}
            onChange={handleMultiSelectChange('ranges')}
            selectedItems={filters.ranges}
          />
          {isMobile && (
            <Chips
              list={selectedRangeItems}
              handleDelete={handleDelete('ranges')}
            />
          )}
          <Divider css={{ width: '100%' }} />
        </Grid>
        {isMobile || (
          <Grid item xs css={{ alignSelf: 'center' }}>
            <ResetButton
              variant="contained"
              disableElevation
              disableRipple
              disableFocusRipple
              startIcon={<ReplayIcon />}
              size="small"
              onClick={handleReset}
            >
              {t('challenges.declaration.resetFilters')}
            </ResetButton>
          </Grid>
        )}
      </Grid>
      {!!numberOfProducts && (
        <Grid item xs={12} css={{ marginTop: theme.spacing(2) }}>
          <Typography paragraph gutterBottom align="center" variant="body2">
            {t('challenges.declaration.foundProductInCatalog', {
              numberOfProducts,
            })}
          </Typography>
        </Grid>
      )}
      {isMobile || (
        <Grid container css={{ margin: theme.spacing(2, 0) }}>
          {filters.search && (
            <Grid item>
              <Chips
                list={[{ label: filters.search }]}
                handleDelete={handleResetSearch}
              />
            </Grid>
          )}
          <Grid item>
            <Chips
              list={selectedBrandItems}
              handleDelete={handleDelete('brands')}
            />
          </Grid>
          <Grid item>
            <Chips
              list={selectedRangeItems}
              handleDelete={handleDelete('ranges')}
            />
          </Grid>
        </Grid>
      )}
      {!numberOfProducts && areProductsFiltered && (
        <Typography>{t('challenges.declaration.searchNoProduct')}</Typography>
      )}
      {isMobile && (
        <Grid container alignContent="center" justifyContent="center">
          <PrimaryButton
            variant="contained"
            color="primary"
            css={{ minWidth: 125, borderRadius: 20, margin: 0 }}
            onClick={onConfirm}
          >
            {t('core.submit')}
          </PrimaryButton>
          <IconButton
            color="primary"
            aria-label="reset filters"
            onClick={handleReset}
            style={{ color: theme.colors.paletteFromBO.main }}
          >
            <ReplayIcon fontSize="large" />
          </IconButton>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default Filter;
